// §0.5.8 Provider Color Tag Settings — lets each provider customize the label for each color tag.
// Stored at user-settings/{userId}, field: colorTagLabels.

import { Box, Button, Flex, Input, Text, VStack } from '@chakra-ui/react'
import { useAuth } from '@hb/react'
import {
  COLOR_TAG_COLORS,
  ColorTagColor,
  DEFAULT_COLOR_TAG_LABELS,
  USER_SETTINGS,
} from '@hb/shared'
import { doc, setDoc } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { db } from '../backend/db/db'
import { useDocument } from '../backend/db/hooks/useDocument'

interface ColorTagConfig {
  colorTagLabels?: Partial<Record<ColorTagColor, string>>
}

const useColorTagConfig = (userId: string | undefined) =>
  useDocument<ColorTagConfig>(USER_SETTINGS, userId ?? null)

const ColorRow = ({
  color,
  savedLabel,
  onSave,
}: {
  color: ColorTagColor
  savedLabel: string
  onSave: (color: ColorTagColor, label: string) => Promise<void>
}) => {
  const [label, setLabel] = useState(savedLabel)
  const [saving, setSaving] = useState(false)
  const isDirty = label !== savedLabel

  const handleSave = useCallback(async () => {
    setSaving(true)
    await onSave(color, label)
    setSaving(false)
  }, [color, label, onSave])

  return (
    <Flex align="center" gap={3}>
      <Box w={4} h={4} borderRadius="full" bg={`${color}.400`} flexShrink={0} />
      <Input
        size="sm"
        value={label}
        onChange={e => setLabel(e.target.value)}
        maxW="220px"
        onKeyDown={e => {
          if (e.key === 'Enter' && isDirty) handleSave()
        }}
        fontFamily="Hero-New"
      />
      {isDirty ? (
        <Button size="xs" colorScheme="teal" isLoading={saving} onClick={handleSave}>
          Save
        </Button>
      ) : null}
    </Flex>
  )
}

export const ColorTagSettings = () => {
  const { authUser } = useAuth()
  const { data } = useColorTagConfig(authUser?.uid)

  const labels: Record<ColorTagColor, string> = useMemo(() => {
    const saved = data?.colorTagLabels ?? {}
    return Object.fromEntries(
      COLOR_TAG_COLORS.map(c => [c, saved[c] ?? DEFAULT_COLOR_TAG_LABELS[c]]),
    ) as Record<ColorTagColor, string>
  }, [data])

  const handleSave = useCallback(
    async (color: ColorTagColor, label: string) => {
      if (!authUser?.uid) return
      await setDoc(
        doc(db, USER_SETTINGS, authUser.uid),
        { colorTagLabels: { [color]: label } },
        { merge: true },
      )
    },
    [authUser?.uid],
  )

  if (!authUser?.uid) return null

  return (
    <Box>
      <Text
        fontFamily="Hero-New"
        fontWeight={600}
        fontSize="sm"
        mb={3}
        color="gray.700"
        _dark={{ color: 'gray.300' }}>
        My Color Tag Labels
      </Text>
      <Text fontSize="xs" color="gray.500" mb={3} lineHeight={1.5}>
        Color tags appear as small dots on patient rows. Customize their labels to match your
        workflow.
      </Text>
      <VStack align="flex-start" spacing={2}>
        {COLOR_TAG_COLORS.map(c => (
          <ColorRow key={c} color={c} savedLabel={labels[c]} onSave={handleSave} />
        ))}
      </VStack>
    </Box>
  )
}
